/**
 * Construye el cuerpo del PATCH de un pedido a partir del borrador del diálogo de edición.
 *
 * Vive fuera del componente y es una función pura porque el detalle del pedido llega en
 * snake_case (`due_date`) y la API habla camelCase (`dueDate`): ese desajuste ya provocó un 400
 * en el que cualquier edición con la cantidad intacta dejaba de funcionar. Con test, no vuelve.
 */
export interface ScheduleDraft {
  quantity: number;
  due_date: string;
}

export interface OrderLineDraft {
  id?: string;
  modelId: string;
  quantity: string;
  unitPrice: string;
  configuration?: Record<string, unknown>;
  schedules?: ScheduleDraft[];
}

export interface OrderPatchBody {
  promisedDate: string | null;
  notes: string | null;
  lines?: {
    modelId: string;
    quantity: number;
    unitPrice: number;
    configuration: Record<string, unknown>;
    schedules?: { quantity: number; dueDate: string }[];
  }[];
}

/** ¿Cambió algo de las líneas respecto de lo que se cargó del pedido? */
export function lineasCambiadas(validas: OrderLineDraft[], originales: OrderLineDraft[]): boolean {
  if (validas.length !== originales.length) return true;
  return validas.some((line) => {
    const antes = originales.find((original) => original.id === line.id);
    if (!antes) return true;
    return antes.modelId !== line.modelId
      || Number(antes.quantity) !== Number(line.quantity)
      || Number(antes.unitPrice) !== Number(line.unitPrice);
  });
}

export function buildOrderPatch(input: {
  promisedDate: string;
  notes: string;
  validas: OrderLineDraft[];
  originales: OrderLineDraft[];
}): OrderPatchBody {
  const { promisedDate, notes, validas, originales } = input;
  const cuerpo: OrderPatchBody = {
    promisedDate: promisedDate || null,
    notes: notes || null
  };
  // Sin cambios en las líneas no se mandan: reenviarlas borraría sus hitos y su configuración.
  if (!lineasCambiadas(validas, originales)) return cuerpo;

  cuerpo.lines = validas.map((line) => {
    const antes = originales.find((original) => original.id === line.id);
    const mismaCantidad = Boolean(antes) && Number(antes!.quantity) === Number(line.quantity);
    const mismoModelo = antes?.modelId === line.modelId;
    const conservaHitos = Boolean(antes?.schedules?.length) && mismaCantidad;
    return {
      modelId: line.modelId,
      quantity: Number(line.quantity),
      unitPrice: Number(line.unitPrice),
      // La configuración de otro modelo no aplica: se vacía.
      configuration: mismoModelo ? (line.configuration ?? {}) : {},
      ...(conservaHitos
        ? { schedules: (line.schedules ?? []).map((schedule) => ({ quantity: schedule.quantity, dueDate: schedule.due_date })) }
        : {})
    };
  });
  return cuerpo;
}

/** ¿Se pierde el reparto de entregas de alguna línea al cambiar la cantidad? */
export function pierdeReparto(validas: OrderLineDraft[], originales: OrderLineDraft[]): boolean {
  return validas.some((line) => {
    const antes = originales.find((original) => original.id === line.id);
    return Boolean(antes) && (antes!.schedules?.length ?? 0) > 1
      && Number(antes!.quantity) !== Number(line.quantity);
  });
}
